import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from './Button.jsx';
import { useCreateTranche, useUpdateTranche } from '../hooks/useAssociateAccounts.js';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

function Field({ label, children, error, required }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return { label: '', opened_at: todayIso(), notes: '', status: 'open' };
}

function formFromTranche(tranche) {
  if (!tranche) return emptyForm();
  return {
    label:     tranche.label ?? '',
    opened_at: tranche.opened_at ?? todayIso(),
    notes:     tranche.notes ?? '',
    status:    tranche.status ?? 'open',
  };
}

function validate(form) {
  const errors = {};
  if (!form.label.trim()) errors.label = 'Libellé requis';
  if (!form.opened_at)    errors.opened_at = 'Date d\'ouverture requise';
  return errors;
}

/**
 * AssociateTrancheForm — slide-over modal pour créer ou éditer une tranche
 * d'un compte courant d'associé.
 *
 * Props :
 *   open      : boolean
 *   onClose   : () => void
 *   accountId : string — UUID du compte parent (requis)
 *   tranche   : object | undefined — si fourni, mode édition
 */
export default function AssociateTrancheForm({ open, onClose, accountId, tranche }) {
  const isEdit = Boolean(tranche);
  const createTranche = useCreateTranche();
  const updateTranche = useUpdateTranche();
  const mutation = isEdit ? updateTranche : createTranche;

  const [form, setForm]     = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(formFromTranche(tranche));
      setErrors({});
      mutation.reset?.();
    }
  }, [open, tranche?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (isEdit) {
      const payload = { notes: form.notes.trim() || null, status: form.status };
      // label est modifiable en édition (unicité par compte garantie par le backend)
      payload.label = form.label.trim();
      await mutation.mutateAsync({ accountId, trancheId: tranche.id, ...payload });
    } else {
      const payload = {
        accountId,
        label:     form.label.trim(),
        opened_at: form.opened_at || null,
        notes:     form.notes.trim() || null,
      };
      await mutation.mutateAsync(payload);
    }

    onClose?.();
  }

  const isDisabled = mutation.isPending;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/30
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Modifier la tranche' : 'Nouvelle tranche'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[480px] lg:rounded-none lg:max-h-none
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier la tranche' : 'Nouvelle tranche'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          <Field label="Libellé" error={errors.label} required>
            <input
              type="text"
              className={inputClass}
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="Ex : Apport janvier 2024"
              disabled={isDisabled}
            />
          </Field>

          {!isEdit && (
            <Field label="Date d'ouverture" error={errors.opened_at} required>
              <input
                type="date"
                className={inputClass}
                value={form.opened_at}
                onChange={(e) => set('opened_at', e.target.value)}
                disabled={isDisabled}
              />
            </Field>
          )}

          {isEdit && (
            <Field label="Statut">
              <select
                className={inputClass}
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                disabled={isDisabled}
              >
                <option value="open">Ouverte</option>
                <option value="closed">Clôturée</option>
              </select>
            </Field>
          )}

          <Field label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Informations complémentaires…"
              disabled={isDisabled}
            />
          </Field>

          {mutation.error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {mutation.error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-safe">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {isEdit ? 'Enregistrer' : 'Créer la tranche'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
